// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import * as yup from "yup";
import {
  INTERVIEW_OVERALL_DECISION,
  SCORE_CARD_CLASSIFICATION_LIST,
} from "@config/constant";
import { Interview, Score } from "@slices/interviewSlice/model";
import { addInterviewScore } from "@slices/interviewSlice/interview";
import { RootState, useAppDispatch, useAppSelector } from "@slices/store";
import LoadingButton from "@mui/lab/LoadingButton";
import { RequestState, Role } from "@utils/types";
import CloseConfirmationModal from "@component/common/CloseConfirmationModal";
import { hasRequiredRole } from "@utils/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

interface ScoreCardForm {
  overallDecision: string;
  notes: string;
}
interface ScoreCard {
  scoreCardTitle: string;
  score: number | null;
}
interface AddScoreModalProps {
  clickedInterview: Interview;
  scoreFormModal: boolean;
  handleCloseScoreFormModal: () => void;
  onSuccessRefresh: () => void;
  hideCancelButton?: boolean;
}

const AddScoreModal = ({
  clickedInterview,
  handleCloseScoreFormModal,
  scoreFormModal,
  onSuccessRefresh,
  hideCancelButton = false,
}: AddScoreModalProps) => {
  const dispatch = useAppDispatch();
  const interviewRequestState = useAppSelector(
    (state: RootState) => state.interview.state,
  );
  const userRoles = useAppSelector((state: RootState) => state.auth.roles);

  const [scoreCardError, setScoreCardError] = useState<boolean>(false);
  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);
  const [scoreCards, setScoreCards] = useState<ScoreCard[]>(
    clickedInterview.scoreCards,
  );
  const userEmail = useAppSelector(
    (state: RootState) => state.auth.decodedIdToken?.email,
  );

  useEffect(() => {
    if (scoreFormModal) {
      setScoreCards(clickedInterview.scoreCards);
      setScoreCardError(false);
      formik.resetForm();
    }
  }, [scoreFormModal, clickedInterview.scoreCards]);

  const scoreCardTitles: string[] = clickedInterview.scoreCards.map(
    (s) => s.scoreCardTitle,
  );

  const handleChange = (rowIndex: number, cardTitle: string) => {
    setScoreCardError(false);
    setScoreCards((prev) =>
      prev.map((scoreCard) =>
        cardTitle === scoreCard.scoreCardTitle
          ? { ...scoreCard, score: rowIndex }
          : scoreCard,
      ),
    );
  };

  const handleChecked = (scoreIndex: number, cardTitle: string): boolean => {
    for (let i = 0; i < scoreCards.length; i++) {
      if (
        scoreCards[i].scoreCardTitle === cardTitle &&
        scoreCards[i].score === scoreIndex
      )
        return true;
    }
    return false;
  };

  const isScoreCardError = (): boolean => {
    const isError = scoreCards.find((item) => item.score === null)
      ? true
      : false;
    setScoreCardError(isError);
    return isError;
  };

  const validationSchema = yup.object({
    overallDecision: yup.string().required("Required").typeError("Required"),
    notes: yup.string().required("Required").typeError("Required"),
  });
  const formik = useFormik<ScoreCardForm>({
    initialValues: {
      overallDecision: "",
      notes: "",
    },
    validationSchema: validationSchema,
    onSubmit: async () => {
      if (isScoreCardError()) return;
      const formData: Score = {
        candidateVacancyId: clickedInterview.candidateVacancyId,
        scoreCards: scoreCards,
        overallDecision: formik.values.overallDecision,
        note: formik.values.notes || null,
      };
      const result = await dispatch(
        addInterviewScore({ id: clickedInterview.id, payload: formData }),
      );
      if (addInterviewScore.fulfilled.match(result)) {
        handleCloseScoreFormModal();
        setScoreCardError(false);
        onSuccessRefresh();
      }
    },
  });

  const handleCancel = () => {
    setOpenConfirmationModal(true);
  };

  const handleConfirmClose = () => {
    setScoreCards(clickedInterview.scoreCards);
    setScoreCardError(false);
    formik.resetForm();
    handleCloseScoreFormModal();
  };

  return (
    <>
      <Dialog
        open={scoreFormModal}
        onClose={hideCancelButton ? () => {} : handleCancel}
        disableEscapeKeyDown={hideCancelButton}
        componentsProps={{
          backdrop: {
            onClick: hideCancelButton ? (e) => e.stopPropagation() : undefined,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h4" sx={{ fontWeight: "600" }}>
            Candidate Evaluation
          </Typography>
          <Divider sx={{ mb: 1.5 }} />

          <Grid container spacing={0.5}>
            {/* name */}
            <Grid item xs={0.8}>
              <PersonIcon
                fontSize="small"
                sx={{ color: (theme) => theme.palette.grey[700] }}
              />
            </Grid>
            <Grid item xs={8.2}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                {clickedInterview.id} - {clickedInterview.candidateName}
              </Typography>
            </Grid>
            <Grid item xs={3} sx={{ display: "flex", justifyContent: "end" }}>
              <Button
                size="small"
                variant="outlined"
                target="_blank"
                href={clickedInterview.resume}
                startIcon={<OpenInNewIcon fontSize="small" />}
                disabled={
                  !(clickedInterview.interviewerEmail === userEmail) &&
                  !hasRequiredRole(userRoles, [
                    Role.HR_ROLE,
                    Role.HIRING_MANAGER_ROLE,
                  ])
                }
                sx={{ px: 1, py: 0 }}
              >
                Resume
              </Button>
            </Grid>
            {/* date */}
            <Grid item xs={0.8}>
              <CalendarMonthIcon
                fontSize="small"
                sx={{ color: (theme) => theme.palette.grey[700] }}
              />
            </Grid>
            <Grid item xs={11}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                {clickedInterview.startTime
                  ? dayjs
                      .utc(clickedInterview.startTime)
                      .tz(dayjs.tz.guess())
                      .format("MMM DD, YYYY")
                  : "-"}
              </Typography>
            </Grid>
            {/* time range */}
            <Grid item xs={0.8}>
              <AccessTimeIcon
                fontSize="small"
                sx={{ color: (theme) => theme.palette.grey[700] }}
              />
            </Grid>
            <Grid item xs={11}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                {clickedInterview.startTime && clickedInterview.endTime
                  ? `${dayjs
                      .utc(clickedInterview.startTime)
                      .tz(dayjs.tz.guess())
                      .format("hh:mm A")} - ${dayjs
                      .utc(clickedInterview.endTime)
                      .tz(dayjs.tz.guess())
                      .format("hh:mm A")}`
                  : "-"}
              </Typography>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Box>
            {scoreCardTitles.length > 0 && (
              <>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "none",
                    border: "1px solid",
                    borderColor: scoreCardError
                      ? (theme) => theme.palette.error.main
                      : (theme) => theme.palette.divider,
                  }}
                >
                  <Table
                    sx={{
                      minWidth: 500,
                      padding: 0,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.secondary.dark
                          : theme.palette.primary.contrastText,
                    }}
                    aria-label="simple table"
                  >
                    <TableHead sx={{ padding: 0 }}>
                      <TableRow>
                        <TableCell>
                          <b>Criteria</b>
                        </TableCell>
                        {SCORE_CARD_CLASSIFICATION_LIST.map((score, index) => (
                          <TableCell
                            align="center"
                            key={index}
                            sx={{ padding: 1 }}
                          >
                            <b>{score}</b>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scoreCardTitles.map((cardTitle, rowIndex) => (
                        <TableRow
                          key={rowIndex}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {cardTitle}
                          </TableCell>
                          {SCORE_CARD_CLASSIFICATION_LIST.map(
                            (_, scoreIndex) => (
                              <TableCell
                                align="center"
                                key={scoreIndex}
                                sx={{ padding: 0 }}
                              >
                                <FormControlLabel
                                  value={scoreIndex}
                                  control={<Radio size="small" />}
                                  label={null}
                                  checked={handleChecked(scoreIndex, cardTitle)}
                                  onChange={() => {
                                    handleChange(scoreIndex, cardTitle);
                                  }}
                                  sx={{ margin: 0 }}
                                />
                              </TableCell>
                            ),
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Grid container marginTop={0} spacing={2}>
              {/* ---------overall rec------- */}
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.overallDecision &&
                    Boolean(formik.errors.overallDecision)
                  }
                >
                  <InputLabel>Overall decision *</InputLabel>

                  <Select
                    id="overallDecision"
                    name="overallDecision"
                    label="Overall decision *"
                    value={formik.values.overallDecision}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {INTERVIEW_OVERALL_DECISION.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>

                  {formik.touched.overallDecision &&
                    formik.errors.overallDecision && (
                      <FormHelperText>
                        {formik.errors.overallDecision}
                      </FormHelperText>
                    )}
                </FormControl>
              </Grid>
              {/* ---------notes------- */}
              <Grid item xs={12}>
                <TextField
                  id="notes"
                  name="notes"
                  fullWidth
                  size="small"
                  type="text"
                  label="Feedback *"
                  multiline
                  maxRows={8}
                  rows={8}
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.notes && Boolean(formik.errors.notes)}
                  helperText={formik.touched.notes && formik.errors.notes}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          {!hideCancelButton && (
            <Button
              type="submit"
              variant="outlined"
              size="small"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          )}
          <LoadingButton
            variant="contained"
            size="small"
            onClick={() => {
              isScoreCardError();
              formik.handleSubmit();
            }}
            loading={interviewRequestState === RequestState.LOADING}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
      {!hideCancelButton && (
        <CloseConfirmationModal
          handleClose={handleConfirmClose}
          openConfirmationModal={openConfirmationModal}
          setOpenConfirmationModal={setOpenConfirmationModal}
        />
      )}
    </>
  );
};

export default AddScoreModal;
